import api from 'api'
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query'

import { AdminPropertyAd, PropertyAd, PropertyAdBasicInfoDTO } from 'types'
import { PropertyAdFilterOptions, PropertyAdUpdateStatusDTO } from './types'

import { AxiosError, AxiosResponse } from 'axios'

const TIME = 1000 * 60 * 1 // 1 min

const SERVICE_NAME = `PropertyAd`

const QueryKeys = {
  count: `${SERVICE_NAME}#count`,
  all: `${SERVICE_NAME}#all`,
  single: `${SERVICE_NAME}#single`,
  basicInfo: `${SERVICE_NAME}#basicInfo`,
  countBasicInfo: `${SERVICE_NAME}#countBasicInfo`,
} as const

const MutationKeys = {
  count: `${SERVICE_NAME}#countMutation`,
  deactivate: `${SERVICE_NAME}#deactivate`,
  create: `${SERVICE_NAME}#create`,
  update: `${SERVICE_NAME}#update`,
  delete: `${SERVICE_NAME}#delete`,
  statusUpdate: `${SERVICE_NAME}#statusUpdate`,
} as const

const CONTROLLER_PATH = '/app/spourgiti/admin/propertyAd'

// Search by Criteria
export const updateStatus = async (
  propertyAd: AdminPropertyAd
): Promise<AdminPropertyAd> => {
  const { data }: AxiosResponse<AdminPropertyAd> = await api({
    method: 'PUT',
    url: `${CONTROLLER_PATH}/update-status/${propertyAd.id}`,
    data: propertyAd,
  })
  return data
}

export const useUpdateStatus = (
  options: Omit<
    UseMutationOptions<AdminPropertyAd, AxiosError, AdminPropertyAd>,
    'mutationKey' | 'mutationFn'
  > = {}
) =>
  useMutation(MutationKeys.deactivate, updateStatus, {
    ...options,
  } as Omit<UseMutationOptions<AdminPropertyAd, AxiosError, AdminPropertyAd>, 'mutationKey' | 'mutationFn'>)

// COUNT all
export const getCountPropertyAds = async (
  options: PropertyAdFilterOptions
): Promise<number> => {
  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/countByCriteria`,
    data: options,
  })
  return data
}

export const useCountPropertyAds = (
  filterOptions: PropertyAdFilterOptions = {},
  queryOptions: UseQueryOptions<number, AxiosError, number, QueryKey> = {}
) => {
  return useQuery(
    [QueryKeys.count, filterOptions],
    () => getCountPropertyAds(filterOptions),
    {
      keepPreviousData: true,
      staleTime: TIME,
      cacheTime: TIME,
      ...queryOptions,
    } as Omit<
      UseQueryOptions<number, AxiosError, number, QueryKey>,
      'queryKey' | 'queryFn'
    >
  )
}

// GET all
export const getPropertyAds = async (
  page: number,
  pageSize: number,
  filters: PropertyAdFilterOptions = {},
  sortOptions: PropertyAdFilterOptions = {}
): Promise<AdminPropertyAd[]> => {
  const { data } = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/search-by-criteria`,
    data: {
      pageNumber: page,
      pageSize,
      ...filters,
      ...sortOptions,
    },
  })

  return data
}
export const usePropertyAds = (
  page: number,
  pageSize: number,
  filters: PropertyAdFilterOptions = {},
  sortOptions: PropertyAdFilterOptions = {},
  queryOptions: UseQueryOptions<
    AdminPropertyAd[],
    AxiosError,
    AdminPropertyAd[],
    QueryKey
  > = {}
) => {
  return useQuery(
    [QueryKeys.all, page, pageSize, filters, sortOptions],
    () => getPropertyAds(page, pageSize, filters, sortOptions),
    {
      keepPreviousData: true,
      staleTime: TIME,
      cacheTime: TIME,
      ...queryOptions,
    } as Omit<
      UseQueryOptions<
        AdminPropertyAd[],
        AxiosError,
        AdminPropertyAd[],
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >
  )
}

// COUNT all BasicInfo
export const getCountBasicInfoPropertyAds = async (
  options: PropertyAdFilterOptions
): Promise<number> => {
  const { data }: AxiosResponse<number> = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/basic-info/count-by-criteria`,
    data: options,
  })
  return data
}

export const useCountBasicInfoPropertyAds = (
  filterOptions: PropertyAdFilterOptions = {},
  queryOptions: UseQueryOptions<number, AxiosError, number, QueryKey> = {}
) => {
  return useQuery(
    [QueryKeys.countBasicInfo, filterOptions],
    () => getCountBasicInfoPropertyAds(filterOptions),
    {
      keepPreviousData: true,
      staleTime: TIME,
      cacheTime: 0,
      ...queryOptions,
    } as Omit<
      UseQueryOptions<number, AxiosError, number, QueryKey>,
      'queryKey' | 'queryFn'
    >
  )
}

// GET all BasicInfo
export const getBasicInfoPropertyAds = async (
  page: number,
  pageSize: number,
  filters: PropertyAdFilterOptions = {},
  sortOptions: PropertyAdFilterOptions = {}
): Promise<PropertyAdBasicInfoDTO[]> => {
  const { data }: AxiosResponse<PropertyAdBasicInfoDTO[]> = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/basic-info/search-by-criteria`,
    data: {
      pageNumber: page,
      pageSize,
      ...filters,
      ...sortOptions,
    },
  })

  return data
}
export const useBasicInfoPropertyAds = (
  page: number,
  pageSize: number,
  filters: PropertyAdFilterOptions = {},
  sortOptions: PropertyAdFilterOptions = {},
  queryOptions: UseQueryOptions<
    PropertyAdBasicInfoDTO,
    AxiosError,
    PropertyAdBasicInfoDTO,
    QueryKey
  > = {}
) => {
  return useQuery(
    [QueryKeys.basicInfo, page, pageSize, filters, sortOptions],
    () => getBasicInfoPropertyAds(page, pageSize, filters, sortOptions),
    {
      keepPreviousData: true,
      staleTime: TIME,
      cacheTime: 0,
      ...queryOptions,
    } as Omit<
      UseQueryOptions<
        PropertyAdBasicInfoDTO[],
        AxiosError,
        PropertyAdBasicInfoDTO[],
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >
  )
}

// Count Property Ad
export const countPropertyAds = async (payload: any): Promise<number> => {
  const { data }: AxiosResponse<number> = await api({
    method: 'post',
    url: `${CONTROLLER_PATH}/countByCriteria`,
    data: {
      ...payload,
    },
  })

  return data
}
export const useCountPropertyAd = (
  options: Omit<
    UseMutationOptions<number, AxiosError, any>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.count, countPropertyAds, {
    ...options,
  })
}

// GET single
export const getPropertyAd = async (id?: string): Promise<AdminPropertyAd> => {
  const { data }: AxiosResponse<AdminPropertyAd> = await api({
    method: 'get',
    url: `${CONTROLLER_PATH}/${id}`,
  })

  return data
}

export const usePropertyAd = (
  id: string | undefined,
  queryOptions: UseQueryOptions<
    AdminPropertyAd,
    AxiosError,
    AdminPropertyAd,
    QueryKey
  > = {}
) => {
  return useQuery<AdminPropertyAd, AxiosError>(
    [QueryKeys.single, id],
    () => getPropertyAd(id),
    {
      staleTime: TIME,
      cacheTime: TIME,
      ...queryOptions,
    } as Omit<
      UseQueryOptions<AdminPropertyAd, AxiosError, AdminPropertyAd, QueryKey>,
      'queryKey' | 'queryFn'
    >
  )
}

// create
export const createPropertyAd = async (payload: AdminPropertyAd) => {
  const { data }: AxiosResponse<AdminPropertyAd> = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/`,
    data: payload,
  })
  return data
}

export const useCreatePropertyAd = (
  options: Omit<
    UseMutationOptions<AdminPropertyAd, AxiosError, AdminPropertyAd>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.create, createPropertyAd, {
    ...options,
  } as Omit<UseMutationOptions<AdminPropertyAd, AxiosError, AdminPropertyAd>, 'mutationKey' | 'mutationFn'>)
}

// update
export const updatePropertyAd = async ({ id, payload }) => {
  const { data }: AxiosResponse<AdminPropertyAd> = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/${id}`,
    data: payload,
  })
  return data
}

export const useUpdatePropertyAd = (
  options: Omit<
    UseMutationOptions<
      AdminPropertyAd,
      AxiosError,
      { id: string; payload: AdminPropertyAd }
    >,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.update, updatePropertyAd, {
    ...options,
  } as Omit<UseMutationOptions<AdminPropertyAd, AxiosError, { id: string; payload: AdminPropertyAd }>, 'mutationKey' | 'mutationFn'>)
}

// UPDATE v2
export const updatePropertyAdStatus = async (
  propertyAdStatus: PropertyAdUpdateStatusDTO
) => {
  const { data }: AxiosResponse<PropertyAd> = await api({
    method: 'put',
    url: `${CONTROLLER_PATH}/update-status/v2/${propertyAdStatus.id}/${propertyAdStatus.status}`,
  })
  return data
}

export const useUpdatePropertyAdStatus = (
  options: Omit<
    UseMutationOptions<PropertyAd, AxiosError, PropertyAdUpdateStatusDTO>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.statusUpdate, updatePropertyAdStatus, {
    ...options,
  } as Omit<UseMutationOptions<PropertyAd, AxiosError, PropertyAdUpdateStatusDTO>, 'mutationKey' | 'mutationFn'>)
}

// DELETE
export const deletePropertyAd = async (id: number): Promise<Response> => {
  const { data } = await api({
    method: 'delete',
    url: `${CONTROLLER_PATH}/${id}`,
  })
  return data
}

export const useDeletePropertyAd = (
  options: Omit<
    UseMutationOptions<number, AxiosError, number>,
    'mutationKey' | 'mutationFn'
  > = {}
) => {
  return useMutation(MutationKeys.delete, deletePropertyAd, {
    ...options,
  } as Omit<UseMutationOptions<Response, AxiosError, number>, 'mutationKey' | 'mutationFn'>)
}

export const PropertyAdService = {
  countPropertyAds,
  updateStatus,
  updatePropertyAd,
  updatePropertyAdStatus,
  deletePropertyAd,
  getBasicInfoPropertyAds,
  getCountBasicInfoPropertyAds,
  getCountPropertyAds,
  getPropertyAds,
  getPropertyAd,
  hooks: {
    useCountPropertyAd,
    useUpdateStatus,
    useUpdatePropertyAd,
    useCreatePropertyAd,
    useUpdatePropertyAdStatus,
    useDeletePropertyAd,
    useBasicInfoPropertyAds,
    useCountBasicInfoPropertyAds,
  },
  keys: { QueryKeys, MutationKeys },
}
export default PropertyAdService
